import {
  DepositForfeitureOutcomeStatus,
  DepositRequestStatus,
  type IDepositRequest,
  type ISchedulingEvent,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  Timestamp,
  toMoment,
  toTimestamp,
  type DocumentReference,
  type Transaction,
  type WithRef,
} from '@principle-theorem/shared';

export enum DepositTransitionClaimType {
  Capture = 'capture',
  Forfeiture = 'forfeiture',
  Release = 'release',
}

interface IBaseDepositTransitionClaimParams {
  depositRequestRef: DocumentReference<IDepositRequest>;
  validate?: (
    depositRequest: WithRef<IDepositRequest>,
    transaction: Transaction
  ) => Promise<boolean | void> | boolean | void;
}

interface ICaptureDepositTransitionClaimParams
  extends IBaseDepositTransitionClaimParams {
  type: DepositTransitionClaimType.Capture;
}

interface IForfeitureDepositTransitionClaimParams
  extends IBaseDepositTransitionClaimParams {
  type: DepositTransitionClaimType.Forfeiture;
  schedulingEventRef: DocumentReference<ISchedulingEvent>;
}

interface IReleaseDepositTransitionClaimParams
  extends IBaseDepositTransitionClaimParams {
  type: DepositTransitionClaimType.Release;
  claimId: string;
}

type DepositTransitionClaimParams =
  | ICaptureDepositTransitionClaimParams
  | IForfeitureDepositTransitionClaimParams
  | IReleaseDepositTransitionClaimParams;

/**
 * Owns the mutual exclusion between capture, forfeiture, and release on an
 * authorised deposit. They run from independent triggers, so without a
 * transactional claim the same authorisation could fund conflicting outcomes.
 */
export class DepositTransitionClaim {
  /**
   * How long a claim is honoured before another worker may take it. Bounds the
   * damage from a process that dies mid-transition without releasing.
   */
  private static readonly _claimTtlMs = 5 * 60 * 1000;

  /**
   * Takes an exclusive claim on the deposit's next transition, returning the
   * claim timestamp only to the caller that won it so a second worker backs off
   * instead of double-charging.
   */
  static async claim(
    params: DepositTransitionClaimParams
  ): Promise<Timestamp | undefined> {
    const claimedAt = toTimestamp();
    return Firestore.runTransaction(async (transaction) => {
      const depositRequest = await Firestore.getDoc(
        params.depositRequestRef,
        transaction
      );
      if (depositRequest.status !== DepositRequestStatus.Authorised) {
        DepositTransitionClaim._trace('refused: request not authorised', {
          type: params.type,
          status: depositRequest.status,
        });
        return;
      }
      const isEligible = await params.validate?.(depositRequest, transaction);
      if (isEligible === false) {
        DepositTransitionClaim._trace('refused: validate returned false', {
          type: params.type,
        });
        return;
      }

      const captureClaimIsActive =
        depositRequest.captureClaimedAt !== undefined &&
        toMoment(claimedAt).diff(toMoment(depositRequest.captureClaimedAt)) <
          DepositTransitionClaim._claimTtlMs;
      const forfeitureClaimIsActive =
        depositRequest.forfeitureOutcome?.status ===
        DepositForfeitureOutcomeStatus.Pending;
      const releaseClaimIsActive =
        !!depositRequest.releaseClaim &&
        !depositRequest.releaseClaim.finalizedAt;

      if (
        (params.type === DepositTransitionClaimType.Capture &&
          (forfeitureClaimIsActive || releaseClaimIsActive)) ||
        (params.type === DepositTransitionClaimType.Forfeiture &&
          (captureClaimIsActive || releaseClaimIsActive)) ||
        (params.type === DepositTransitionClaimType.Release &&
          (captureClaimIsActive || forfeitureClaimIsActive))
      ) {
        DepositTransitionClaim._trace('refused: conflicting claim active', {
          type: params.type,
          captureClaimIsActive,
          forfeitureClaimIsActive,
          releaseClaimIsActive,
        });
        return;
      }

      if (params.type === DepositTransitionClaimType.Capture) {
        if (captureClaimIsActive || depositRequest.capturedAt) {
          return;
        }
        await Firestore.updateDoc(
          params.depositRequestRef,
          { captureClaimedAt: claimedAt },
          transaction
        );
        return claimedAt;
      }

      if (params.type === DepositTransitionClaimType.Release) {
        if (releaseClaimIsActive) {
          return depositRequest.releaseClaim?.claimId === params.claimId
            ? depositRequest.releaseClaim.claimedAt
            : undefined;
        }
        await Firestore.updateDoc(
          params.depositRequestRef,
          { releaseClaim: { claimId: params.claimId, claimedAt } },
          transaction
        );
        return claimedAt;
      }

      if (forfeitureClaimIsActive) {
        DepositTransitionClaim._trace('refused: forfeiture already pending');
        return;
      }
      await Firestore.updateDoc(
        params.depositRequestRef,
        {
          forfeitureOutcome: {
            schedulingEventRef: params.schedulingEventRef,
            status: DepositForfeitureOutcomeStatus.Pending,
          },
        },
        transaction
      );
      DepositTransitionClaim._trace('won: forfeiture pending written');
      return claimedAt;
    });
  }

  /**
   * Hands a capture claim back when the work did not proceed, so the deposit is
   * retryable immediately rather than blocked for the full claim TTL. Releases
   * only the caller's own claim so a later winner is never clobbered.
   */
  static async releaseCapture(
    depositRequestRef: DocumentReference<IDepositRequest>,
    claimedAt: Timestamp
  ): Promise<void> {
    await Firestore.runTransaction(async (transaction) => {
      const depositRequest = await Firestore.getDoc(
        depositRequestRef,
        transaction
      );
      if (
        !depositRequest.captureClaimedAt ||
        !toMoment(depositRequest.captureClaimedAt).isSame(toMoment(claimedAt))
      ) {
        return;
      }
      await Firestore.updateDoc(
        depositRequestRef,
        { captureClaimedAt: Timestamp.fromMillis(0) },
        transaction
      );
    });
  }

  /**
   * Diagnostic breadcrumb for every exit of `claim`. Uses console because this
   * library has no logger dependency; the prefix keeps it filterable alongside
   * the function-side trace.
   */
  private static _trace(message: string, detail?: object): void {
    // eslint-disable-next-line no-console -- observability breadcrumb, this file has no logger dep.
    console.info(`[deposit-forfeiture-trace] claim ${message}`, detail ?? '');
  }
}
